import { Select } from "@msuite/ui/form";
import { guardString } from "@msuite/utils/strings";
import type { Control, FieldValues, Path } from "react-hook-form";
import { useCrewDefinitions } from "@/hooks/use-crew-definitions";

/** Props Interface */
interface CrewDefinitionPickerProps<T extends FieldValues> {
	control: Control<T>;
	name: Path<T>;
	label: string;
	isRequired: boolean;
	/** Only the crews whose own span covers these days; both ends inclusive. */
	startDate?: string;
	endDate?: string;
}

/**
 * One of the organisation's standing crews.
 *
 * When a span is given the list is narrowed to the crews that exist across all of it, because the
 * server refuses a stretch reaching outside the crew it is on — offering a crew that cannot take
 * the work only produces an error after the form is submitted.
 */
export function CrewDefinitionPicker<T extends FieldValues>({
	control,
	name,
	label,
	isRequired,
	startDate,
	endDate,
}: CrewDefinitionPickerProps<T>) {
	/** Query */
	const { crewDefinitions } = useCrewDefinitions();

	/** Functions */
	function getAvailableCrews() {
		if (!startDate || !endDate) return crewDefinitions;
		const result: typeof crewDefinitions = [];
		for (const crewDefinition of crewDefinitions) {
			if (crewDefinition.startDate > startDate) continue;
			if (crewDefinition.endDate && crewDefinition.endDate < endDate) continue;
			result.push(crewDefinition);
		}
		return result;
	}

	/** Computed */
	const available = getAvailableCrews();

	/** Render */
	return (
		<Select
			label={label}
			control={control}
			name={name}
			isRequired={isRequired}
			isNullable={false}
			options={available.map((crewDefinition) => ({
				label: guardString(crewDefinition.displayName),
				value: crewDefinition.id,
				item: crewDefinition,
			}))}
			itemNotFoundLabel=""
			data-cy="crew-definition-picker"
		/>
	);
}
